import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { HubSpotForm } from "~/components/forms/HubSpotForm";
import { Image } from "~/components/Image";
import { getGatedTalkVideo, loadTalk } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

/**
 * Sets the server-readable unlock cookie for a gated talk. This is what lets
 * getGatedTalkVideo (a separate, no-store server fn call made post-mount)
 * hand back the real video URL -- the initial SSR/hydration payload for this
 * route never contains it, gated or not (see loadTalk in content-fns.ts).
 *
 * Not cryptographically airtight: a client-set cookie can be forged by a
 * determined visitor. This only stops the casual view-source/curl bypass.
 */
function setUnlockedCookie(slug: string) {
  const maxAgeSeconds = 60 * 60 * 24 * 365; // 1 year, matches localStorage's effectively-permanent intent
  document.cookie = `talk-unlocked-${slug}=1; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

export const Route = createFileRoute("/talks/$slug")({
  loader: ({ params }) => loadTalk({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.title,
          description: loaderData.description,
          image: loaderData.featured_image,
          url: `/talks/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/talks/${loaderData.slug}` }) : [],
  }),
  component: TalkSingle,
});

function TalkVideo({ src, title }: { src: string; title: string }) {
  return (
    <div className="mb-8 aspect-video overflow-hidden rounded-lg">
      <iframe
        src={src}
        className="h-full w-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title={title}
      />
    </div>
  );
}

/**
 * Gated state: the webinar poster fills a 16:9 frame, dimmed by a scrim, with
 * the email form centered on top (Wistia "Turnstile" style). On mobile a strict
 * 16:9 is too short for the form, so the frame keeps the poster fill but grows
 * to a min-height; the aspect ratio is only enforced from `sm` up.
 */
function GatedPosterForm({
  poster,
  heading,
  copy,
  formId,
  portalId,
  formCta,
  onSubmitted,
}: {
  poster?: string;
  heading?: string;
  copy?: string;
  formId: string;
  portalId?: string;
  formCta?: string;
  onSubmitted: () => void;
}) {
  return (
    <div className="relative mb-8 overflow-hidden rounded-lg">
      {poster && (
        <Image
          src={poster}
          alt=""
          aria-hidden="true"
          loading="eager"
          sizes="(min-width: 768px) 768px, 100vw"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" />
      <div className="relative flex min-h-[27rem] items-center justify-center px-6 py-10 sm:aspect-video">
        <div className="hs-form-dark hs-form-solid register-form-spacing w-full max-w-md rounded-xl border border-white/10 bg-charcoal/70 p-6 backdrop-blur-sm sm:p-8">
          {(heading || copy) && (
            <div className="mb-5 text-center">
              {heading && (
                <p className="font-display text-2xl font-bold text-white lg:text-3xl">
                  {heading}
                </p>
              )}
              {copy && <p className="mt-2 text-white/80">{copy}</p>}
            </div>
          )}
          <HubSpotForm
            formId={formId}
            portalId={portalId}
            formCta={formCta}
            placeholdersFromLabels
            onSubmitted={onSubmitted}
          />
        </div>
      </div>
    </div>
  );
}

function TalkSingle() {
  const talk = Route.useLoaderData();
  const gated = Boolean(talk.hubspot_form_id);
  const cookieName = `talk-unlocked-${talk.slug}`;

  // SSR renders form-first (revealed=false); a returning visitor who already
  // unlocked is upgraded to revealed on the client via the unlock cookie.
  // For gated talks, `talk.featured_video` is never populated server-side
  // (loadTalk strips it) -- the real URL only arrives via the separate
  // getGatedTalkVideo fetch below, once revealed.
  const [revealed, setRevealed] = useState(false);
  const [gatedVideoUrl, setGatedVideoUrl] = useState<string | null>(null);

  // Flips the UI to "revealed" and, for gated talks, fetches the real video
  // URL out-of-band via a no-store server fn call that reads the unlock
  // cookie server-side. The cookie must already be set before this runs.
  const reveal = useCallback(() => {
    setRevealed(true);
    if (!gated) return;
    getGatedTalkVideo({ data: { slug: talk.slug } })
      .then((res) => setGatedVideoUrl(res.featured_video))
      .catch(() => {
        // Non-fatal: stays revealed with no video; a refresh will retry.
      });
  }, [gated, talk.slug]);

  useEffect(() => {
    if (!gated || typeof window === "undefined") return;
    // Email bypass: a visitor arriving from a HubSpot marketing email carries
    // an _hsenc/_hsmi param. They're already a known contact, so skip the gate.
    const params = new URLSearchParams(window.location.search);
    if (params.has("_hsenc") || params.has("_hsmi")) {
      setUnlockedCookie(talk.slug);
      reveal();
      return;
    }
    const hasUnlockCookie = document.cookie
      .split("; ")
      .some((c) => c === `${cookieName}=1`);
    if (hasUnlockCookie) reveal();
  }, [gated, cookieName, talk.slug, reveal]);

  const handleSubmitted = () => {
    setUnlockedCookie(talk.slug);
    reveal();
  };

  // Ungated talks render `talk.featured_video` as always; gated talks only
  // ever render the separately-fetched `gatedVideoUrl`, once revealed.
  const effectiveVideoSrc = gated ? gatedVideoUrl : talk.featured_video;
  const showVideo = Boolean(effectiveVideoSrc) && (!gated || revealed);

  // Autoplay only when the video appears as the result of an (always
  // user-initiated) gate reveal. Ungated talks keep the plain, non-autoplaying
  // src they render today.
  const videoSrc =
    effectiveVideoSrc && gated && revealed
      ? effectiveVideoSrc +
        (effectiveVideoSrc.includes("?") ? "&" : "?") +
        "autoplay=1"
      : effectiveVideoSrc;

  // Revealed but the getGatedTalkVideo fetch hasn't resolved yet: keep the
  // poster on screen (no form -- the visitor already unlocked it) instead of
  // flashing back to the gate form while the URL loads.
  const awaitingGatedVideo = gated && revealed && !effectiveVideoSrc;

  return (
    <article className="mx-auto max-w-3xl px-4 py-16 md:px-8">
      <div className="mb-6 text-sm text-gray-60">
        <time>
          {new Date(talk.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </time>
        {talk.speaker && (
          <>
            {" "}
            &middot; {talk.speaker}
            {talk.speaker_title && `, ${talk.speaker_title}`}
          </>
        )}
      </div>
      <h1 className="mb-6 font-display text-4xl font-bold leading-tight text-charcoal md:text-5xl">
        {talk.title}
      </h1>

      {showVideo && videoSrc ? (
        <TalkVideo src={videoSrc} title={talk.title} />
      ) : awaitingGatedVideo ? (
        <div className="relative mb-8 aspect-video overflow-hidden rounded-lg bg-gray-6">
          {talk.featured_image && (
            <Image
              src={talk.featured_image}
              alt=""
              aria-hidden="true"
              loading="eager"
              sizes="(min-width: 768px) 768px, 100vw"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
        </div>
      ) : gated && talk.hubspot_form_id ? (
        <GatedPosterForm
          poster={talk.featured_image ?? undefined}
          heading={talk.form_heading ?? undefined}
          copy={talk.form_copy ?? undefined}
          formId={talk.hubspot_form_id}
          portalId={talk.hubspot_portal_id ?? undefined}
          formCta={talk.form_cta ?? undefined}
          onSubmitted={handleSubmitted}
        />
      ) : null}

      <div className="content group" dangerouslySetInnerHTML={{ __html: talk.html }} />
    </article>
  );
}
